import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './MediaViewerModal.css';
import { PostMedia } from '../../../../types/postType';
import deleteIcon from '../../../../assets/icons/delete_all.svg';

interface MediaViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaList: PostMedia[];
  initialIndex: number;
}

const MediaViewerModal: React.FC<MediaViewerModalProps> = ({
  isOpen,
  onClose,
  mediaList,
  initialIndex = 0
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const modalRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number | null>(null);
  const currentXRef = useRef<number | null>(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  useEffect(() => {
    // Add keyboard event listeners when modal is open
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent body scrolling
      console.log('Media viewer opened with:', { mediaList, initialIndex });
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto'; // Restore body scrolling
    };
  }, [isOpen, currentIndex]);

  useEffect(() => {
    console.log('📺 MediaViewerModal props changed:', { 
      isOpen, 
      initialIndex,
      mediaListLength: mediaList?.length || 0,
      currentIndex
    });
  }, [isOpen, initialIndex, mediaList, currentIndex]);

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        goToPrevious();
        break;
      case 'ArrowRight':
        goToNext();
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < mediaList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Touch event handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    currentXRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (startXRef.current === null) return;
    currentXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (startXRef.current === null || currentXRef.current === null) return;
    
    const diff = startXRef.current - currentXRef.current;
    const threshold = 50; // Minimum swipe distance
    
    if (diff > threshold) {
      // Swiped left, go to next
      goToNext();
    } else if (diff < -threshold) {
      // Swiped right, go to previous
      goToPrevious();
    }
    
    // Reset
    startXRef.current = null;
    currentXRef.current = null;
  };

  // Stop propagation to prevent modal closing when clicking media
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!isOpen || !mediaList || mediaList.length === 0) {
    console.log('🚫 MediaViewerModal not rendering because:', { 
      isOpen, 
      hasMediaList: !!mediaList, 
      mediaListLength: mediaList?.length || 0 
    });
    return null;
  }

  console.log('✅ MediaViewerModal rendering with:', {
    currentIndex,
    currentMedia: mediaList[currentIndex],
    hasMultipleMedia: mediaList.length > 1
  });

  const currentMedia = mediaList[currentIndex];
  const hasMultipleMedia = mediaList.length > 1;

  // Using createPortal to render outside the normal document flow
  return createPortal(
    <div className="media-viewer-overlay" onClick={onClose} ref={modalRef}>
      <div 
        className="media-viewer-content" 
        onClick={handleContentClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button className="media-viewer-close" onClick={onClose}>
          <img src={deleteIcon} alt="Close" />
        </button>
        
        {hasMultipleMedia && currentIndex > 0 && (
          <button className="media-viewer-nav media-viewer-prev" onClick={goToPrevious}>
            <span className="arrow-icon">←</span>
          </button>
        )}
        
        <div className="media-viewer-media-container">
          {currentMedia.media_type === 'video' ? (
            <video 
              src={currentMedia.url} 
              controls 
              className="media-viewer-media"
              autoPlay
            />
          ) : (
            <img 
              src={currentMedia.url} 
              alt={`Media ${currentIndex + 1} of ${mediaList.length}`}
              className="media-viewer-media"
            />
          )}
        </div>
        
        {hasMultipleMedia && currentIndex < mediaList.length - 1 && (
          <button className="media-viewer-nav media-viewer-next" onClick={goToNext}>
            <span className="arrow-icon">→</span>
          </button>
        )}
        
        {hasMultipleMedia && (
          <div className="media-viewer-indicator">
            {mediaList.map((_, index) => (
              <div 
                key={index} 
                className={`indicator-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MediaViewerModal; 